import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Alert,
  LinearProgress,
  AlertColor,
  Stack,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Download,
  Description,
  Image as ImageIcon,
  PictureAsPdf,
  DragIndicator,
  Refresh,
  CheckCircle,
} from '@mui/icons-material';
import {
  radioReportService,
  ParseAndGenerateResponse,
  RadioReportError,
  MAX_FILE_SIZE_MB,
  MAX_FILE_SIZE_BYTES,
  MAX_FILES_COUNT,
  ALLOWED_FILE_EXTENSIONS,
} from './radioReport.service';
import { useToast } from '../../contexts/ToastContext';

interface SelectedFile {
  file: File;
  id: string;
}

const RadioReportParser: React.FC = () => {
  const { showToast } = useToast();
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [result, setResult] = useState<ParseAndGenerateResponse | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragItemRef = useRef<number | null>(null);
  const dragOverItemRef = useRef<number | null>(null);

  // Get file icon based on type
  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <PictureAsPdf sx={{ color: '#E53935' }} />;
    if (
      ['jpg', 'jpeg', 'png', 'bmp', 'tiff', 'gif', 'heic', 'heif'].includes(
        ext || ''
      )
    )
      return <ImageIcon sx={{ color: '#43A047' }} />;
    if (ext === 'docx') return <Description sx={{ color: '#1976D2' }} />;
    return <Description />;
  };

  // Validate file extension
  const isValidFileExtension = (file: File): boolean => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    return ALLOWED_FILE_EXTENSIONS.includes(ext);
  };

  // Check if file exceeds size limit
  const isFileTooLarge = (file: File): boolean => {
    return file.size > MAX_FILE_SIZE_BYTES;
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Handle file selection
  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const validFiles: SelectedFile[] = [];
      const invalidExtensionFiles: string[] = [];
      const tooLargeFiles: string[] = [];

      // Check if adding these files would exceed the max count
      const currentCount = selectedFiles.length;
      const newCount = currentCount + fileArray.length;

      if (newCount > MAX_FILES_COUNT) {
        showToast(
          `Cannot add ${fileArray.length} file(s). Maximum ${MAX_FILES_COUNT} files allowed. You currently have ${currentCount} file(s) selected.`,
          'warning'
        );
        return;
      }

      fileArray.forEach(file => {
        if (!isValidFileExtension(file)) {
          invalidExtensionFiles.push(file.name);
        } else if (isFileTooLarge(file)) {
          tooLargeFiles.push(`${file.name} (${formatFileSize(file.size)})`);
        } else {
          validFiles.push({
            file,
            id: Math.random().toString(36).substring(7),
          });
        }
      });

      // Show appropriate error messages
      const errorMessages: string[] = [];

      if (invalidExtensionFiles.length > 0) {
        errorMessages.push(
          `Invalid file type: ${invalidExtensionFiles.join(', ')}. Allowed: PDF, Images, DOCX`
        );
      }

      if (tooLargeFiles.length > 0) {
        errorMessages.push(
          `File(s) too large (max ${MAX_FILE_SIZE_MB}MB): ${tooLargeFiles.join(', ')}`
        );
      }

      if (errorMessages.length > 0) {
        showToast(errorMessages.join('\n'), 'warning');
      }

      if (validFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
      }
    },
    [selectedFiles.length]
  );

  // Drag and drop handlers for file upload area
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  // File input change handler
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove file from list
  const handleRemoveFile = (id: string) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== id));
  };

  // Clear all files
  const handleClearAll = () => {
    setSelectedFiles([]);
    setResult(null);
  };

  // Drag reorder handlers
  const handleDragStart = (index: number) => {
    dragItemRef.current = index;
  };

  const handleDragEnter = (index: number) => {
    dragOverItemRef.current = index;
  };

  const handleDragEnd = () => {
    if (dragItemRef.current !== null && dragOverItemRef.current !== null) {
      const newFiles = [...selectedFiles];
      const draggedItem = newFiles[dragItemRef.current];
      newFiles.splice(dragItemRef.current, 1);
      newFiles.splice(dragOverItemRef.current, 0, draggedItem);
      setSelectedFiles(newFiles);
    }
    dragItemRef.current = null;
    dragOverItemRef.current = null;
  };

  // Process files
  const handleProcessFiles = async () => {
    if (selectedFiles.length === 0) {
      showToast('Please select at least one file', 'warning');
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);
    setProgressMessage(`Uploading ${selectedFiles.length} file(s)...`);

    try {
      // Step 1: Upload files
      const files = selectedFiles.map(sf => sf.file);
      const uploadResponse = await radioReportService.uploadFiles(files);

      setUploadProgress(40);
      setProgressMessage('Files uploaded. Processing with AI...');
      setIsUploading(false);
      setIsProcessing(true);

      // Step 2: Parse and generate
      const filePaths = uploadResponse.uploaded_files.map(f => f.file_path);
      const processResponse = await radioReportService.parseAndGenerate(
        uploadResponse.session_id,
        filePaths
      );

      setUploadProgress(100);
      setProgressMessage('Report generated successfully!');
      setResult(processResponse);

      showToast(
        `Successfully processed ${processResponse.total_files_processed} file(s)!`,
        'success'
      );
    } catch (error) {
      console.error('Processing error:', error);

      let errorMessage = 'Processing failed. Please try again.';
      let severity: AlertColor = 'error';

      if (error instanceof RadioReportError) {
        errorMessage = error.message;
        // Use warning for validation errors, error for server/network issues
        severity = error.errorType === 'validation' ? 'warning' : 'error';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      showToast(errorMessage, severity);
    } finally {
      setIsUploading(false);
      setIsProcessing(false);
      setUploadProgress(0);
      setProgressMessage('');
    }
  };

  // Download report
  const handleDownloadReport = async () => {
    if (!result?.download_url) {
      return;
    }

    try {
      // window.open()/a plain navigation can't attach the Authorization
      // header report.clearscan.ca now requires, so this has to go
      // through the same authenticated fetch as upload/parse - fetch the
      // file as a blob and hand the browser a local object URL to save
      // instead of navigating there directly.
      const blob = await radioReportService.downloadReport(result.filename);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      const errorMessage =
        error instanceof RadioReportError
          ? error.message
          : 'Failed to download the report. Please try again.';
      showToast(errorMessage, 'error');
    }
  };

  // Start over
  const handleStartOver = () => {
    setSelectedFiles([]);
    setResult(null);
    setUploadProgress(0);
    setProgressMessage('');
  };

  const isLoading = isUploading || isProcessing;

  return (
    <Box sx={{ p: 0 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Radio Report Parser
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload medical radiology reports (PDF, images, or Word documents) to
          parse and generate branded ClearScan reports using AI
        </Typography>
      </Box>

      {/* Main Content */}
      {!result ? (
        <Card
          elevation={0}
          sx={{ borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)', mb: 4 }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Upload Area */}
            <Paper
              variant="outlined"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !isLoading && fileInputRef.current?.click()}
              sx={{
                p: 5,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: isDragOver ? 'primary.main' : 'divider',
                borderRadius: 3,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                bgcolor: isDragOver ? 'primary.50' : 'background.paper',
                transition: 'all 0.2s ease',
                opacity: isLoading ? 0.6 : 1,
                '&:hover': {
                  borderColor: isLoading ? 'divider' : 'primary.main',
                  bgcolor: isLoading ? 'background.paper' : 'action.hover',
                },
              }}
            >
              <CloudUpload
                sx={{
                  fontSize: 64,
                  color: isDragOver ? 'primary.main' : 'text.secondary',
                  mb: 2,
                }}
              />
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Drag and drop files here, or click to browse
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Supported formats: PDF, Images (JPG, PNG, HEIC, etc.), Word
                documents (.docx)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Maximum file size: {MAX_FILE_SIZE_MB}MB per file
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can upload up to {MAX_FILES_COUNT} files at once
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.bmp,.tiff,.gif,.heic,.heif,.docx"
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
                disabled={isLoading}
              />
            </Paper>

            {/* File List */}
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Selected Files
                    <Chip
                      label={selectedFiles.length}
                      size="small"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={handleClearAll}
                    disabled={isLoading}
                    startIcon={<Delete />}
                  >
                    Clear All
                  </Button>
                </Box>

                <List
                  sx={{
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  {selectedFiles.map((sf, index) => (
                    <React.Fragment key={sf.id}>
                      <ListItem
                        draggable={!isLoading}
                        onDragStart={() => handleDragStart(index)}
                        onDragEnter={() => handleDragEnter(index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={e => e.preventDefault()}
                        sx={{
                          cursor: isLoading ? 'default' : 'grab',
                          bgcolor: 'background.paper',
                          borderLeft: '4px solid',
                          borderLeftColor: 'primary.main',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                          '&:active': {
                            cursor: 'grabbing',
                          },
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={() => handleRemoveFile(sf.id)}
                            disabled={isLoading}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        }
                      >
                        <IconButton
                          size="small"
                          sx={{ mr: 1, cursor: 'grab' }}
                          disabled={isLoading}
                        >
                          <DragIndicator />
                        </IconButton>
                        <Chip
                          label={`File ${index + 1}`}
                          size="small"
                          sx={{
                            mr: 2,
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontWeight: 600,
                          }}
                        />
                        <Box sx={{ mr: 2 }}>{getFileIcon(sf.file.name)}</Box>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={500}>
                              {sf.file.name}
                            </Typography>
                          }
                          secondary={formatFileSize(sf.file.size)}
                        />
                      </ListItem>
                      {index < selectedFiles.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}

            {/* Progress */}
            {isLoading && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress
                  variant={isProcessing ? 'indeterminate' : 'determinate'}
                  value={uploadProgress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, textAlign: 'center' }}
                >
                  {progressMessage}
                </Typography>
              </Box>
            )}

            {/* Action Buttons */}
            {selectedFiles.length > 0 && !isLoading && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleProcessFiles}
                  startIcon={<CloudUpload />}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Process All Files ({selectedFiles.length})
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Results Card */
        <Card
          elevation={0}
          sx={{ borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)', mb: 4 }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircle
                sx={{ fontSize: 80, color: 'success.main', mb: 2 }}
              />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Report Generated Successfully!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {result.total_files_processed} file(s) have been processed and
                merged into a single branded report.
              </Typography>

              {result.merged_report?.patient?.name && (
                <Alert
                  severity="info"
                  sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
                >
                  <Typography variant="body2">
                    <strong>Patient:</strong>{' '}
                    {result.merged_report.patient.name
                      ?.split(', ')
                      .reverse()
                      .join(' ')}
                  </Typography>
                </Alert>
              )}

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleDownloadReport}
                  startIcon={<Download />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Download Report
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleStartOver}
                  startIcon={<Refresh />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Process More Files
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.08)',
          bgcolor: 'grey.50',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            How it works
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              1. Upload a single medical radiology report (PDF, DOCX, or
              multiple images for one report)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              2. Click "Process Report" to parse and extract data using AI
            </Typography>
            <Typography variant="body2" color="text.secondary">
              3. Download the generated branded ClearScan report (PDF format)
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RadioReportParser;
